// components/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import './styles/AdminPanel.css';
import config from '../configs/config';
import {
  FaUsers, FaBuilding, FaProjectDiagram,
  FaEdit, FaTimes, FaPlus, FaSearch, FaSpinner,
  FaShareAlt, FaClock, FaArrowLeft, FaEnvelope,
  FaToggleOn, FaToggleOff
} from 'react-icons/fa';

const FEATURE_KEYS = ['home', 'scripting', 'datacheck', 'datatables', 'banner', 'weighting', 'dataanalysis', 'dashboard', 'qnrdesign'];
const EXPORT_OPTIONS = ['csv', 'excel', 'pdf'];

const AdminPanel = () => {
  const [organizations, setOrganizations] = useState([]);
  const [totalStats, setTotalStats] = useState({ users: 0, projects: 0, shares: 0 });
  const [loading, setLoading] = useState(true);

  // Drill-down state — null = org list, 'org' = org detail, 'user' = user projects, 'project' = project users
  const [view, setView] = useState('list'); // 'list' | 'org' | 'user' | 'project'
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgProjects, setOrgProjects] = useState([]);
  const [orgDetailTab, setOrgDetailTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  // Store initial counts from list view
  const [initialUserCount, setInitialUserCount] = useState(0);
  const [initialProjectCount, setInitialProjectCount] = useState(0);

  // Loading states for org details
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const [orgProjectsLoading, setOrgProjectsLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [userProjectsLoading, setUserProjectsLoading] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [projectUsersLoading, setProjectUsersLoading] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emptyOrg = {
    name: '', domain: '', logo_url: '', favicon_url: '',
    features: { home: true, scripting: true, datacheck: true, datatables: true, banner: true, weighting: true, dataanalysis: true, dashboard: true, qnrdesign: false },
    data_tables_feature: {}, scripting_feature: {},
    download_features: ['csv', 'excel', 'pdf'],
    datatables_exports: ['csv', 'excel', 'pdf']
  };
  const [newOrg, setNewOrg] = useState(emptyOrg);
  const [editOrg, setEditOrg] = useState({ id: null, name: '', domain: '', logo_url: '', favicon_url: '', features: {} });

  const h = () => ({ 'Content-Type': 'application/json' });

  /* ── Fetch ── */
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data);
        setTotalStats({
          users: data.reduce((s, o) => s + (o.user_count || 0), 0),
          projects: data.reduce((s, o) => s + (o.project_count || 0), 0),
          shares: 0
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchOrgUsers = async (orgId) => {
    setOrgUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/users`, { headers: h(), credentials: 'include' });
      const data = res.ok ? await res.json() : [];
      setOrgUsers(data);
    } catch (e) { setOrgUsers([]); }
    finally { setOrgUsersLoading(false); }
  };

  const fetchOrgProjects = async (orgId) => {
    setOrgProjectsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/projects`, { headers: h(), credentials: 'include' });
      const data = res.ok ? await res.json() : [];
      setOrgProjects(data);
    } catch (e) { setOrgProjects([]); }
    finally { setOrgProjectsLoading(false); }
  };

  const fetchUserProjects = async (email, orgId) => {
    setUserProjectsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/users/${encodeURIComponent(email)}/projects`, { headers: h(), credentials: 'include' });
      setUserProjects(res.ok ? await res.json() : []);
    } catch (e) { setUserProjects([]); }
    finally { setUserProjectsLoading(false); }
  };

  const fetchProjectUsers = async (projectId) => {
    setProjectUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/projects/${projectId}/users`, { headers: h(), credentials: 'include' });
      setProjectUsers(res.ok ? await res.json() : []);
    } catch (e) { setProjectUsers([]); }
    finally { setProjectUsersLoading(false); }
  };

  /* ── Navigation ── */
  const openOrg = async (org) => {
    setInitialUserCount(org.user_count || 0);
    setInitialProjectCount(org.project_count || 0);
    setSelectedOrg(org);
    setOrgDetailTab('users');
    setSearchTerm('');
    setView('org');
    setOrgUsers([]);
    setOrgProjects([]);
    await Promise.all([fetchOrgUsers(org.id), fetchOrgProjects(org.id)]);
  };

  const openUser = async (user) => {
    setSelectedUser(user);
    setView('user');
    await fetchUserProjects(user.email, selectedOrg.id);
  };

  const openProject = async (project) => {
    setSelectedProject(project);
    setView('project');
    await fetchProjectUsers(project.id);
  };

  const goBack = () => {
    if (view === 'user') {
      setSelectedUser(null);
      setUserProjects([]);
      setView('org');
      setOrgDetailTab('users');
    }
    if (view === 'project') {
      setSelectedProject(null);
      setProjectUsers([]);
      setView('org');
      setOrgDetailTab('projects');
    }
    if (view === 'org') {
      setSelectedOrg(null);
      setOrgUsers([]);
      setOrgProjects([]);
      setInitialUserCount(0);
      setInitialProjectCount(0);
      setView('list');
    }
  };

  /* ── CRUD ── */
  const handleAdd = async () => {
    if (!newOrg.name || !newOrg.domain) { alert('Name aur domain required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations`, { method: 'POST', headers: h(), credentials: 'include', body: JSON.stringify(newOrg) });
      if (res.ok) {
        setShowAddModal(false);
        setNewOrg(emptyOrg);
        await loadAll();
      }
      else { const e = await res.json(); alert(e.error || 'Failed'); }
    } catch (e) { alert('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!editOrg.name || !editOrg.domain) { alert('Name aur domain required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${editOrg.id}`, { method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify(editOrg) });
      if (res.ok) {
        setShowEditModal(false);
        await loadAll();
        if (selectedOrg?.id === editOrg.id) setSelectedOrg(prev => ({ ...prev, ...editOrg }));
      } else { const e = await res.json(); alert(e.error || 'Failed'); }
    } catch (e) { alert('Failed'); }
    finally { setSubmitting(false); }
  };

  const toggleExport = (key, field, setFn) => {
    setFn(prev => ({ ...prev, [field]: prev[field].includes(key) ? prev[field].filter(x => x !== key) : [...prev[field], key] }));
  };

  /* ── Filtered lists ── */
  const filteredUsers = orgUsers.filter(u => u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredProjects = orgProjects.filter(p => p.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  /* ── Breadcrumb ── */
  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span className={view === 'list' ? 'bc-active' : 'bc-link'} onClick={() => view !== 'list' && goBack()}>
        <FaBuilding /> Organizations
      </span>
      {(view === 'org' || view === 'user' || view === 'project') && (
        <><span className="bc-sep">›</span>
          <span className={view === 'org' ? 'bc-active' : 'bc-link'} onClick={() => (view === 'user' || view === 'project') && goBack()}>
            {selectedOrg?.name}
          </span></>
      )}
      {view === 'user' && (<><span className="bc-sep">›</span><span className="bc-active">{selectedUser?.full_name}</span></>)}
      {view === 'project' && (<><span className="bc-sep">›</span><span className="bc-active">{selectedProject?.project_name}</span></>)}
    </div>
  );

  if (loading) return (
    <div className="ap-loading"><FaSpinner className="spinner" /><p>Loading...</p></div>
  );

  return (
    <div className="admin-panel">
      {/* Top header */}
      <div className="ap-header">
        <div>
          <h1 className="ap-title">Admin Panel</h1>
          <Breadcrumb />
        </div>
        <div className="ap-summary">
          <div className="ap-pill"><FaBuilding /><span>{organizations.length}</span><label>Orgs</label></div>
          <div className="ap-pill"><FaUsers /><span>{totalStats.users}</span><label>Users</label></div>
          <div className="ap-pill"><FaProjectDiagram /><span>{totalStats.projects}</span><label>Projects</label></div>
        </div>
      </div>

      {/* VIEW: LIST */}
      {view === 'list' && (
        <div className="ap-body">
          <div className="ap-row">
            <h2 className="ap-section-title">All Organizations</h2>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}><FaPlus /> Add Organization</button>
          </div>
          <div className="org-list">
            {organizations.map(org => (
              <div key={org.id} className="org-row" onClick={() => openOrg(org)}>
                <div className="org-row-left">
                  {org.favicon_url ? <img src={org.favicon_url} alt={org.name} className="org-av" /> : <div className="org-av org-av-placeholder">{org.name.charAt(0)}</div>}
                  <div>
                    <div className="org-row-name">{org.name}</div>
                    <div className="org-row-domain">{org.domain}</div>
                  </div>
                </div>
                <div className="org-row-stats">
                  <span><FaUsers /> {org.user_count || 0} users</span>
                  <span><FaProjectDiagram /> {org.project_count || 0} projects</span>
                </div>
                <button className="btn-icon" onClick={e => {
                  e.stopPropagation();
                  setEditOrg({ id: org.id, name: org.name, domain: org.domain, logo_url: org.favicon_url || '', favicon_url: org.favicon_url || '', features: org.features || {} });
                  setShowEditModal(true);
                }}>
                  <FaEdit /> <span className="btn-text">Edit</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIEW: ORG DETAIL */}
      {view === 'org' && (
        <div className="ap-body">
          <button className="back-btn" onClick={goBack}><FaArrowLeft /> Back</button>
          <div className="ap-row" style={{ marginBottom: 20 }}>
            <div className="org-detail-head">
              {selectedOrg.favicon_url ? <img src={selectedOrg.favicon_url} alt={selectedOrg.name} className="org-av-lg" /> : <div className="org-av-lg org-av-placeholder">{selectedOrg.name.charAt(0)}</div>}
              <div>
                <h2 className="ap-section-title" style={{ margin: 0 }}>{selectedOrg.name}</h2>
                <p className="org-row-domain">{selectedOrg.domain}</p>
              </div>
            </div>
          </div>

          <div className="detail-tabs">
            <button className={orgDetailTab === 'users' ? 'active' : ''} onClick={() => { setOrgDetailTab('users'); setSearchTerm(''); }}>
              <FaUsers /> Users ({orgUsersLoading && orgUsers.length === 0 ? initialUserCount : orgUsers.length})
            </button>
            <button className={orgDetailTab === 'projects' ? 'active' : ''} onClick={() => { setOrgDetailTab('projects'); setSearchTerm(''); }}>
              <FaProjectDiagram /> Projects ({orgProjectsLoading && orgProjects.length === 0 ? initialProjectCount : orgProjects.length})
            </button>
          </div>

          <div className="search-bar">
            <FaSearch />
            <input type="text" placeholder={`Search ${orgDetailTab}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>

          {/* Users Tab */}
          {orgDetailTab === 'users' && (
            <div className="item-list">
              {orgUsersLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching users... (Total: {initialUserCount})</p></div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div key={user.email} className="item-row clickable" onClick={() => openUser(user)}>
                    <div className="user-av">{(user.full_name?.[0] || user.email?.[0] || 'U').toUpperCase()}</div>
                    <div className="item-info">
                      <div className="item-name">{user.full_name || 'Unknown'}</div>
                      <div className="item-sub"><FaEnvelope /> {user.email}</div>
                    </div>
                    <div className="item-stats">
                      <span><FaProjectDiagram /> {user.owned_projects || 0} owned</span>
                      <span><FaShareAlt /> {user.shared_projects || 0} shared</span>
                    </div>
                    <span className="chevron">›</span>
                  </div>
                ))
              ) : <div className="empty-msg">No users found</div>}
            </div>
          )}

          {/* Projects Tab - with id and created_at */}
          {orgDetailTab === 'projects' && (
            <div className="item-list">
              {orgProjectsLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching projects... (Total: {initialProjectCount})</p></div>
              ) : filteredProjects.length > 0 ? (
                filteredProjects.map(project => (
                  <div key={project.id} className="item-row clickable" onClick={() => openProject(project)}>
                    <div className="proj-av"><FaProjectDiagram /></div>
                    <div className="item-info">
                      <div className="item-name">
                        {project.project_name}
                        {project.id && (
                          <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9ca3af', fontWeight: 'normal' }}>
                            (ID: {project.id})
                          </span>
                        )}
                      </div>
                      <div className="item-sub">
                        Client: {project.client_name || 'N/A'} · Owner: {project.owner || 'N/A'}
                        {project.created_at && (
                          <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FaClock size={10} /> {new Date(project.created_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="item-stats">
                      <span><FaShareAlt /> {project.shared_with_count || 0} shared</span>
                    </div>
                    <span className="chevron">›</span>
                  </div>
                ))
              ) : <div className="empty-msg">No projects found</div>}
            </div>
          )}
        </div>
      )}

      {/* VIEW: USER → PROJECTS */}
      {view === 'user' && (
        <div className="ap-body">
          <button className="back-btn" onClick={goBack}><FaArrowLeft /> Back to Users</button>
          <div className="detail-hero">
            <div className="user-av user-av-lg">{(selectedUser.full_name?.[0] || selectedUser.email?.[0] || 'U').toUpperCase()}</div>
            <div>
              <h2 className="ap-section-title" style={{ margin: 0 }}>{selectedUser.full_name}</h2>
              <p className="org-row-domain">{selectedUser.email}</p>
              <div className="hero-stats">
                <span><FaProjectDiagram /> {selectedUser.owned_projects || 0} owned</span>
                <span><FaShareAlt /> {selectedUser.shared_projects || 0} shared</span>
              </div>
            </div>
          </div>

          <h3 className="ap-sub-title">Projects</h3>
          {userProjectsLoading ? (
            <div className="loading-state"><FaSpinner className="spinner" /><p>Loading projects...</p></div>
          ) : (
            <div className="item-list">
              {userProjects.length > 0 ? userProjects.map(p => (
                <div key={p.id} className="item-row">
                  <div className="proj-av"><FaProjectDiagram /></div>
                  <div className="item-info">
                    <div className="item-name">
                      {p.project_name}
                      {p.id && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9ca3af', fontWeight: 'normal' }}>
                          (ID: {p.id})
                        </span>
                      )}
                    </div>
                    <div className="item-sub">
                      Client: {p.client_name || 'N/A'} · DB: {p.database_name || 'N/A'}
                      {p.created_at && (
                        <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <FaClock size={10} /> {new Date(p.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="item-stats">
                    <span className={`role-badge ${p.role === 'owner' ? 'role-owner' : 'role-shared'}`}>{p.role === 'owner' ? 'Owner' : 'Shared'}</span>
                    <span><FaShareAlt /> {p.shared_with_count || 0}</span>
                  </div>
                </div>
              )) : <div className="empty-msg">No projects found</div>}
            </div>
          )}
        </div>
      )}

      {/* VIEW: PROJECT → USERS */}
      {view === 'project' && (
        <div className="ap-body">
          <button className="back-btn" onClick={goBack}><FaArrowLeft /> Back to Projects</button>
          <div className="detail-hero">
            <div className="proj-av proj-av-lg"><FaProjectDiagram /></div>
            <div>
              <h2 className="ap-section-title" style={{ margin: 0 }}>
                {selectedProject.project_name}
                {selectedProject.id && (
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#9ca3af', fontWeight: 'normal' }}>
                    (ID: {selectedProject.id})
                  </span>
                )}
              </h2>
              <p className="org-row-domain">
                Client: {selectedProject.client_name || 'N/A'} · DB: {selectedProject.database_name || 'N/A'}
              </p>
              <div className="hero-stats">
                {selectedProject.created_at && (
                  <span><FaClock /> Created: {new Date(selectedProject.created_at).toLocaleString()}</span>
                )}
                <span><FaShareAlt /> Shared with {selectedProject.shared_with_count || 0}</span>
              </div>
            </div>
          </div>

          <h3 className="ap-sub-title">Users with Access</h3>
          {projectUsersLoading ? (
            <div className="loading-state"><FaSpinner className="spinner" /><p>Loading users...</p></div>
          ) : (
            <div className="item-list">
              {projectUsers.length > 0 ? projectUsers.map(u => (
                <div key={u.email} className="item-row">
                  <div className="user-av">{(u.full_name?.[0] || u.email?.[0] || 'U').toUpperCase()}</div>
                  <div className="item-info">
                    <div className="item-name">{u.full_name || 'Unknown'}</div>
                    <div className="item-sub"><FaEnvelope /> {u.email}</div>
                  </div>
                  <div className="item-stats">
                    <span className={`role-badge ${u.role === 'owner' ? 'role-owner' : 'role-shared'}`}>{u.role === 'owner' ? 'Owner' : 'Shared'}</span>
                  </div>
                </div>
              )) : <div className="empty-msg">No users found</div>}
            </div>
          )}
        </div>
      )}

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="emodal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaPlus /> Add Organization</h3>
              <button className="emodal-close" onClick={() => setShowAddModal(false)}><FaTimes /></button>
            </div>
            <div className="emodal-body">
              <div className="emodal-section-title">Basic Info</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input type="text" value={newOrg.name} onChange={e => setNewOrg({ ...newOrg, name: e.target.value })} placeholder="e.g. Excavate Research" />
                </div>
                <div className="form-group">
                  <label>Domain *</label>
                  <input type="text" value={newOrg.domain} onChange={e => setNewOrg({ ...newOrg, domain: e.target.value })} placeholder="e.g. excavateresearch.com" />
                  <small>Users with this domain auto-mapped</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input type="url" value={newOrg.favicon_url} onChange={e => setNewOrg({ ...newOrg, logo_url: e.target.value })} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label>Favicon URL</label>
                  <input type="url" value={newOrg.favicon_url} onChange={e => setNewOrg({ ...newOrg, favicon_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <div className="emodal-section-title">Features</div>
              <div className="features-grid">
                {FEATURE_KEYS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => setNewOrg(prev => ({ ...prev, features: { ...prev.features, [key]: !prev.features[key] } }))}>
                    <span className="feature-name">{key}</span>
                    {newOrg.features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>
              <div className="emodal-section-title">Download Formats</div>
              <div className="export-checks">
                {EXPORT_OPTIONS.map(opt => (
                  <label key={opt} className="check-label">
                    <input type="checkbox" checked={newOrg.download_features.includes(opt)} onChange={() => toggleExport(opt, 'download_features', setNewOrg)} /> {opt.toUpperCase()}
                  </label>
                ))}
              </div>
              <div className="emodal-section-title">Datatables Exports</div>
              <div className="export-checks">
                {EXPORT_OPTIONS.map(opt => (
                  <label key={opt} className="check-label">
                    <input type="checkbox" checked={newOrg.datatables_exports.includes(opt)} onChange={() => toggleExport(opt, 'datatables_exports', setNewOrg)} /> {opt.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>
            <div className="emodal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleAdd} disabled={submitting}>
                {submitting ? <FaSpinner className="spinner-small" /> : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="emodal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaEdit /> Edit Organization</h3>
              <button className="emodal-close" onClick={() => setShowEditModal(false)}><FaTimes /></button>
            </div>
            <div className="emodal-body">
              <div className="emodal-section-title">Basic Info</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input type="text" value={editOrg.name} onChange={e => setEditOrg({ ...editOrg, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Domain *</label>
                  <input type="text" value={editOrg.domain} onChange={e => setEditOrg({ ...editOrg, domain: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input type="url" value={editOrg.favicon_url} onChange={e => setEditOrg({ ...editOrg, logo_url: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Favicon URL</label>
                  <input type="url" value={editOrg.favicon_url} onChange={e => setEditOrg({ ...editOrg, favicon_url: e.target.value })} />
                </div>
              </div>
              <div className="emodal-section-title">Features</div>
              <div className="features-grid">
                {FEATURE_KEYS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => setEditOrg(prev => ({ ...prev, features: { ...prev.features, [key]: !prev.features[key] } }))}>
                    <span className="feature-name">{key}</span>
                    {editOrg.features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleEdit} disabled={submitting}>
                {submitting ? <FaSpinner className="spinner-small" /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;